/**
 * @file useVisualEditorPreview.ts
 *
 * @fileoverview Shows content generated in the visual editor before the page
 * is saved.
 *
 * The visual editor shows this page in an iframe next to the Drupal form.
 * Drupal posts into the iframe when a generation starts, so the section shows
 * as loading where the content will appear, and again when it ends, with the
 * generated values (jobiqo_ai_paragraphs/js/visual_editor_preview.js). The
 * rows are patched on screen; nothing is saved, so a reload shows the saved
 * page again.
 */

import { useEffect, useState } from 'react';

/**
 * The message type Drupal posts.
 */
export const VISUAL_EDITOR_PREVIEW_MESSAGE = 'JOBIQO_AI_PARAGRAPH_PREVIEW';

/**
 * Which posted field becomes which row property, per paragraph bundle.
 *
 * Only these bundles are previewed; a row of any other bundle keeps its saved
 * content.
 */
const PREVIEW_FIELDS: Record<string, Record<string, string>> = {
  ai_generated_site_paragraph: { field_ai_body: 'body' },
};

export interface VisualEditorPreview {
  /**
   * The UUID of the paragraph generated into.
   */
  uuid: string;
  /**
   * The paragraph bundle.
   */
  bundle: string;
  /**
   * The row position, or null when the paragraph is not a row of the page.
   */
  delta: number | null;
  /**
   * Whether the generation is still running.
   *
   * A generation that produced nothing, because it was refused or failed, ends
   * as idle, so a section never keeps showing as loading.
   */
  state: 'pending' | 'ready' | 'idle';
  /**
   * The generated values, keyed by field machine name.
   */
  fields?: Record<string, string>;
}

/**
 * Whether posted data is a preview message of the expected shape.
 *
 * @param {any} data
 *   The posted data.
 * @returns {boolean}
 *   Whether the data can be used as a preview.
 */
const isPreviewMessage = (data: any): data is VisualEditorPreview => {
  return (
    !!data &&
    data.type === VISUAL_EDITOR_PREVIEW_MESSAGE &&
    typeof data.uuid === 'string' &&
    typeof data.bundle === 'string' &&
    (data.delta === null || Number.isInteger(data.delta)) &&
    ['pending', 'ready', 'idle'].includes(data.state) &&
    (data.state !== 'ready' ||
      (!!data.fields &&
        typeof data.fields === 'object' &&
        Object.values(data.fields).every((value) => typeof value === 'string')))
  );
};

/**
 * Collect the previews Drupal posts while the page is in the visual editor.
 *
 * @param {boolean} enabled
 *   Whether the page is shown in the visual editor.
 * @returns {Record<string, VisualEditorPreview>}
 *   The latest preview per paragraph UUID.
 */
export const useVisualEditorPreview = (
  enabled: boolean,
): Record<string, VisualEditorPreview> => {
  const [previews, setPreviews] = useState<Record<string, VisualEditorPreview>>(
    {},
  );

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || window.parent === window) {
      return undefined;
    }

    const onMessage = (event: MessageEvent) => {
      // Only the page framing this one may send a preview. The
      // Content-Security-Policy frame-ancestors directive limits who can frame
      // the frontend to the backend, so the origin needs no separate setting.
      if (event.source !== window.parent || !isPreviewMessage(event.data)) {
        return;
      }
      const { uuid, bundle, delta, state, fields } = event.data;
      setPreviews((current) => ({
        ...current,
        [uuid]: { uuid, bundle, delta, state, fields },
      }));
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [enabled]);

  return previews;
};

/**
 * Apply the previews to the rows of a landing page.
 *
 * A row being generated into is marked as loading, and gets the generated
 * values once they arrive. A previewed section that is not on the page yet,
 * because it was added in the form and never saved, is inserted at its
 * position, so the wait is visible where the section will appear.
 *
 * @param {T[]} rows
 *   The saved rows.
 * @param {Record<string, VisualEditorPreview>} previews
 *   The previews, keyed by paragraph UUID.
 * @returns {T[]}
 *   The rows to render.
 */
export const applyVisualEditorPreviews = <
  T extends { uuid?: string | null; bundle?: string | null }
>(
  rows: T[],
  previews: Record<string, VisualEditorPreview>,
): T[] => {
  const usable = Object.values(previews).filter(
    (preview) => !!PREVIEW_FIELDS[preview.bundle],
  );
  if (usable.length === 0) {
    return rows;
  }

  const toProps = (
    preview: VisualEditorPreview,
  ): Record<string, string | boolean> => {
    const props: Record<string, string | boolean> = {
      aiPreviewLoading: preview.state === 'pending',
    };
    Object.entries(PREVIEW_FIELDS[preview.bundle]).forEach(([field, prop]) => {
      if (preview.fields && field in preview.fields) {
        props[prop] = preview.fields[field];
      }
    });
    return props;
  };

  const byUuid = Object.fromEntries(usable.map((p) => [p.uuid, p]));
  const result = rows.map((row) =>
    row.uuid && byUuid[row.uuid]
      ? { ...row, ...toProps(byUuid[row.uuid]) }
      : row,
  );

  const saved = new Set(rows.map((row) => row.uuid));
  usable
    .filter(
      (preview) =>
        !saved.has(preview.uuid) &&
        preview.delta !== null &&
        preview.state !== 'idle',
    )
    .sort((a, b) => (a.delta as number) - (b.delta as number))
    .forEach((preview) => {
      const row = ({
        uuid: preview.uuid,
        bundle: preview.bundle,
        ...toProps(preview),
      } as unknown) as T;
      result.splice(Math.min(preview.delta as number, result.length), 0, row);
    });

  return result;
};
